<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
    @click="$emit('close')"
  >
    <div class="bg-neutral-900 rounded-lg w-full max-w-2xl p-6" @click.stop>
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-xl font-bold text-white">Render Settings</h2>
        <button
          @click="$emit('close')"
          class="text-gray-400 hover:text-white transition-colors"
        >
          <i class="mdi mdi-close text-xl"></i>
        </button>
      </div>

      <!-- Settings Grid -->
      <div class="grid grid-cols-2 gap-8 mb-6">
        <!-- Resolution Settings -->
        <div>
          <label class="block text-gray-400 text-sm mb-4">Resolution</label>
          <div class="flex items-center space-x-4">
            <div class="flex-1">
              <input
                type="number"
                v-model="localSettings.width"
                @input="handleWidthChange"
                class="w-full bg-neutral-800 text-white px-3 py-2 rounded-md"
                placeholder="Width"
              />
            </div>
            <button
              @click="toggleAspectRatioLock"
              class="text-gray-400 hover:text-white transition-colors"
            >
              <i
                class="mdi"
                :class="
                  aspectRatioLocked
                    ? 'mdi-link-variant'
                    : 'mdi-link-variant-off'
                "
              ></i>
            </button>
            <div class="flex-1">
              <input
                type="number"
                v-model="localSettings.height"
                @input="handleHeightChange"
                class="w-full bg-neutral-800 text-white px-3 py-2 rounded-md"
                placeholder="Height"
              />
            </div>
          </div>
        </div>

        <!-- Quality Settings -->
        <div>
          <div class="flex justify-between items-center mb-4">
            <label class="text-gray-400 text-sm">Quality</label>
            <span class="text-gray-400 text-sm"
              >{{ Math.round(localSettings.quality * 100) }}%</span
            >
          </div>
          <input
            type="range"
            v-model="localSettings.quality"
            min="0.1"
            max="1"
            step="0.1"
            class="w-full accent-[#27bdf4]"
          />
          <div class="flex justify-between text-xs text-gray-500 mt-1">
            <span>Low</span>
            <span>High</span>
          </div>
        </div>

        <!-- FPS Settings -->
        <div>
          <label class="block text-gray-400 text-sm mb-2">Frame Rate</label>
          <select
            v-model="localSettings.fps"
            class="w-full bg-neutral-800 text-white px-3 py-2 rounded-md"
          >
            <option :value="12">12 FPS</option>
            <option :value="24">24 FPS</option>
            <option :value="30">30 FPS</option>
            <option :value="60">60 FPS</option>
          </select>
        </div>
      </div>

      <!-- Progress Section -->
      <div v-if="renderProgress.current > 0" class="mb-6">
        <div class="flex justify-between text-sm text-gray-400 mb-2">
          <span>{{ renderProgress.status || "Processing..." }}</span>
          <span>{{ renderProgress.current }}/{{ renderProgress.total }}</span>
        </div>
        <div class="bg-neutral-800 rounded-full h-2 overflow-hidden">
          <div
            class="h-full bg-[#27bdf4] transition-all duration-300"
            :style="{
              width: `${
                (renderProgress.current / renderProgress.total) * 100
              }%`,
            }"
          ></div>
        </div>
      </div>

      <!-- Modal Actions -->
      <div class="flex justify-end space-x-3">
        <button
          @click="$emit('close')"
          class="px-4 py-2 text-white bg-neutral-800 rounded-md hover:bg-neutral-700"
        >
          {{ renderProgress.current > 0 ? "Cancel Render" : "Cancel" }}
        </button>
        <button
          @click="startRender"
          class="px-4 py-2 text-white bg-[#27bdf4] rounded-md hover:bg-[#1da8dc]"
          :disabled="renderProgress.current > 0"
        >
          <span v-if="renderProgress.current === 0">Start Render</span>
          <span v-else>Rendering...</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
  export default {
    name: "RenderModal",
    props: {
      isOpen: {
        type: Boolean,
        required: true,
      },
      renderProgress: {
        type: Object,
        required: true,
      },
    },
    data() {
      return {
        localSettings: {
          width: 1920,
          height: 1080,
          quality: 0.8,
          fps: 24,
        },
        aspectRatioLocked: true,
        aspectRatio: 16 / 9,
      };
    },
    methods: {
      toggleAspectRatioLock() {
        this.aspectRatioLocked = !this.aspectRatioLocked;
        if (this.aspectRatioLocked) {
          this.aspectRatio =
            this.localSettings.width / this.localSettings.height;
        }
      },
      handleWidthChange() {
        if (this.aspectRatioLocked) {
          this.localSettings.height = Math.round(
            this.localSettings.width / this.aspectRatio
          );
        }
      },
      handleHeightChange() {
        if (this.aspectRatioLocked) {
          this.localSettings.width = Math.round(
            this.localSettings.height * this.aspectRatio
          );
        }
      },
      startRender() {
        this.$emit("start-render", this.localSettings);
      },
    },
  };
</script>

<style scoped>
  input[type="range"] {
    -webkit-appearance: none;
    width: 100%;
    height: 4px;
    border-radius: 2px;
    background: #4a5568;
    outline: none;
  }

  input[type="range"]::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #27bdf4;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  input[type="range"]::-webkit-slider-thumb:hover {
    transform: scale(1.1);
  }

  input[type="range"]::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #27bdf4;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
  }

  input[type="range"]::-moz-range-thumb:hover {
    transform: scale(1.1);
  }
</style>
